import React, { useEffect, useMemo, useState } from "react"
import Icon from "../../../components/AppIcon"
import Button from "../../../components/ui/Button"
import { Card, CardContent } from "../../../components/ui/card"
import { Badge } from "../../../components/ui/badge"
import { listOrgCertificates, getCertificate } from "../../../utils/certHelpers"
import {
  listOrgVerificationEvents,
  enrichVerificationEvents,
  computeVerificationSummary,
} from "../../../utils/verificationEventHelpers"

const inputClass =
  "w-full rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground/60 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"

const StatCard = ({ label, value, icon }) => (
  <Card className="p-4 dark:bg-transparent">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-2xl font-bold text-foreground mt-0.5">{value}</p>
      </div>
      <Icon name={icon} size={18} className="text-muted-foreground" />
    </div>
  </Card>
)

const formatRelativeTime = (timestamp) => {
  const ms = timestamp?.toMillis ? timestamp.toMillis() : null
  if (!ms) return "Just now"
  const days = Math.floor((Date.now() - ms) / 86400000)
  if (days <= 0) {
    const hours = Math.floor((Date.now() - ms) / 3600000)
    return hours <= 0 ? "Just now" : `${hours}h ago`
  }
  if (days === 1) return "Yesterday"
  if (days < 30) return `${days}d ago`
  return new Date(ms).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
}

const VerificationPanel = ({ organization }) => {
  const orgId = organization?.orgId
  const [certificates, setCertificates] = useState([])
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)

  const [lookupId, setLookupId] = useState("")
  const [lookupLoading, setLookupLoading] = useState(false)
  const [lookupResult, setLookupResult] = useState(null) // { certificate } | { notFound: true } | null
  const [lookupError, setLookupError] = useState("")

  const load = async () => {
    if (!orgId) return
    setLoading(true)
    const [{ certificates: certs }, { events: evs }] = await Promise.all([
      listOrgCertificates(orgId),
      listOrgVerificationEvents(orgId),
    ])
    setCertificates(certs || [])
    setEvents(evs || [])
    setLoading(false)
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId])

  const summary = useMemo(() => computeVerificationSummary(events, certificates), [events, certificates])
  const activity = useMemo(() => enrichVerificationEvents(events, certificates), [events, certificates])

  const handleLookup = async (e) => {
    e.preventDefault()
    const certId = lookupId.trim()
    if (!certId) return
    setLookupLoading(true)
    setLookupError("")
    setLookupResult(null)

    const { certificate, error } = await getCertificate(certId, { logEvent: false })
    setLookupLoading(false)

    if (error) {
      setLookupError("Something went wrong looking this up.")
      return
    }
    if (!certificate) {
      setLookupResult({ notFound: true })
      return
    }
    if (certificate.orgId !== orgId) {
      setLookupResult({ notFound: true })
      return
    }
    setLookupResult({ certificate })
  }

  const copyVerificationLink = async (certId) => {
    const url = `${window.location.origin}/verify/${certId}`
    try {
      await navigator.clipboard.writeText(url)
    } catch {
      window.prompt("Copy verification link:", url)
    }
  }

  if (loading) {
    return <div className="p-12 text-center text-muted-foreground text-sm">Loading verification data...</div>
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground max-w-xl">
        Every external check against a credential you've issued, plus a lookup tool for confirming a
        credential's status yourself.
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <StatCard label="Total Verifications" value={summary.totalVerifications} icon="ShieldCheck" />
        <StatCard label="Credentials Verified" value={summary.uniqueCredentialsVerified} icon="Award" />
        <StatCard label="Verification Rate" value={`${summary.verificationRate}%`} icon="TrendingUp" />
      </div>

      {/* Manual lookup tool */}
      <Card className="dark:bg-transparent">
        <CardContent className="p-4 space-y-3">
          <h3 className="font-semibold text-sm text-foreground">Verify a Credential</h3>
          <form onSubmit={handleLookup} className="flex flex-col sm:flex-row gap-2">
            <input
              className={inputClass}
              value={lookupId}
              onChange={(e) => setLookupId(e.target.value)}
              placeholder="Enter a credential ID, e.g. NESO-abc123-XYZ"
            />
            <Button type="submit" loading={lookupLoading} iconName="Search" className="shrink-0">
              Verify
            </Button>
          </form>

          {lookupError && <p className="text-sm text-error">{lookupError}</p>}

          {lookupResult?.notFound && (
            <div className="flex items-center gap-2 text-sm text-error bg-error/10 rounded-md px-3 py-2">
              <Icon name="XCircle" size={16} />
              No credential found with that ID for your organization.
            </div>
          )}

          {lookupResult?.certificate && (
            <div
              className={`rounded-lg p-4 flex items-start justify-between gap-3 ${
                lookupResult.certificate.status === "revoked" ? "bg-error/10" : "bg-success/10"
              }`}
            >
              <div className="flex items-start gap-3">
                <Icon
                  name={lookupResult.certificate.status === "revoked" ? "ShieldOff" : "ShieldCheck"}
                  size={22}
                  className={lookupResult.certificate.status === "revoked" ? "text-error" : "text-success"}
                />
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    {lookupResult.certificate.status === "revoked" ? "Revoked Credential" : "Valid Credential"}
                  </p>
                  <p className="text-sm text-foreground mt-1">{lookupResult.certificate.courseTitle}</p>
                  <p className="text-xs text-muted-foreground">
                    Issued to {lookupResult.certificate.issuedTo} ·{" "}
                    {lookupResult.certificate.issueDate?.slice(0, 10)}
                  </p>
                </div>
              </div>
              <Button
                size="xs"
                variant="ghost"
                iconName="Link"
                onClick={() => copyVerificationLink(lookupResult.certificate.certId)}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Most-verified credentials */}
      {summary.topCertificates.length > 0 && (
        <Card className="dark:bg-transparent">
          <CardContent className="p-4 space-y-3">
            <h3 className="font-semibold text-sm text-foreground">Most Verified Credentials</h3>
            {summary.topCertificates.map((t) => (
              <div key={t.certId} className="flex items-center justify-between py-1.5 text-sm">
                <div className="min-w-0">
                  <p className="text-foreground truncate">
                    {t.certificate?.courseTitle || "Unknown Course"}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {t.certificate?.issuedTo || t.certId}
                  </p>
                </div>
                <span className="text-xs text-muted-foreground shrink-0 ml-3">
                  {t.count} check{t.count === 1 ? "" : "s"}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Activity log */}
      <Card className="dark:bg-transparent">
        <CardContent className="p-4">
          <h3 className="font-semibold text-sm text-foreground mb-3">Verification Activity</h3>
          {activity.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              No external verification checks yet.
            </p>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {activity.slice(0, 100).map((e, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between py-2 border-b border-border last:border-0 text-sm"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon name="Eye" size={13} className="text-muted-foreground shrink-0" />
                    <span className="text-foreground truncate">
                      {e.certificate?.courseTitle || "Unknown Credential"}
                    </span>
                    <Badge variant="secondary" className="text-[10px] shrink-0">
                      {e.certificate?.issuedTo || e.certId}
                    </Badge>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0 ml-2">
                    {formatRelativeTime(e.createdAt)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default VerificationPanel